import { useCallback, useEffect } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { skipToken } from '@reduxjs/toolkit/query';
import { VALUE_CHAIN_SECTION } from 'constants/formFields';
import { useGetNaceCodeTreeQuery } from 'store/slices/dataSets/apis/dataSetsApi';
import { useGetLocationsCountriesQuery } from 'store/slices/geoLocations/apis/geoLocationsApi';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import { useGetProfitCenterDataQuery } from 'store/slices/valueChainMap/apis/valueChainMapApi';
import getProfitCentreDefaultValues from 'utils/formHelpers/getProfitCentreDefaultValues';
import { getCreateProfitCentreSchema } from 'utils/validators';

import type { TCreateProfitCentreSchema } from 'utils/validators';
import type ICreateProfitCentreForm from '../interfaces/ICreateProfitCentreForm';

const useCreateProfitCentreForm = ({
  recordId,
  valueChainSectionCurrent,
  orgStructureData,
}: ICreateProfitCentreForm) => {
  const { data } = useGetProfitCenterDataQuery(
    recordId ? { chainId: `${recordId}` } : skipToken,
    { refetchOnMountOrArgChange: true },
  );

  const { data: naceCodeTreeData } = useGetNaceCodeTreeQuery();
  const { data: userData } = useUserMeQuery();
  const companyCurrency = userData?.data?.company?.currency;

  const { data: locationsCountries } = useGetLocationsCountriesQuery();

  const {
    control,
    handleSubmit,
    formState,
    formState: { isDirty, isLoading },
    reset,
    setError,
    getValues,
    watch,
    setValue,
    trigger,
  } = useForm<TCreateProfitCentreSchema>({
    mode: 'onSubmit',
    resolver: useCallback<Resolver<TCreateProfitCentreSchema>>(
      (values, context, options) => {
        const schema = getCreateProfitCentreSchema(orgStructureData);
        return yupResolver(schema)(values, context, options);
      },
      [orgStructureData],
    ),
    values: getProfitCentreDefaultValues({
      values: data?.data,
      naceCodeTreeData,
      companyCurrency,
      locationsCountries: locationsCountries?.data || [],
      valueChainSectionCurrent,
    }),
  });

  useEffect(() => {
    if (valueChainSectionCurrent) {
      setValue(VALUE_CHAIN_SECTION, {
        value: valueChainSectionCurrent,
        label: valueChainSectionCurrent,
      });
    }
  }, [valueChainSectionCurrent, setValue]);

  const fieldsName = Object.keys(getValues());

  return {
    control,
    handleSubmit,
    isDirty,
    reset,
    isLoading,
    setError,
    fieldsName,
    watch,
    setValue,
    trigger,
    formState,
  };
};

export default useCreateProfitCentreForm;
